// Unified bottom navigation for all roles
// CREATED: Bottom nav that shows role-appropriate tabs
// Operators: Accueil | Incidents | Profil
// Managers: Accueil | Équipe | Incidents | Profil
// Admin/Director: Équipe | Incidents | Profil | Plus
// UPDATED: Extended to support Admin/Director with 4-tab navigation including "Plus" menu
// UPDATED: Renamed "Mes tâches" to "Accueil" with Home icon
// UPDATED: Removed incident report button - reporting is now integrated into Incidents page
// UPDATED: Removed badge indicator from nav items (incident reads feature removed)

import { type UserRole, getRoleTheme } from '../../lib/roleThemes';
import { NavItem } from './AppSidebar';

interface UnifiedBottomNavProps {
  currentView: string;
  onNavigate: (view: string) => void;
  onProfileClick?: () => void;
  onMoreClick?: () => void;
  userRole: UserRole;
  navItems: NavItem[];
}

export function UnifiedBottomNav({
  currentView,
  onNavigate,
  onProfileClick,
  onMoreClick,
  userRole,
  navItems
}: UnifiedBottomNavProps) {
  const theme = getRoleTheme(userRole);

  // Handle navigation with special cases for profile and more menu
  const handleNavigation = (itemId: string) => {
    if (itemId === 'profile' && onProfileClick) {
      onProfileClick();
    } else if (itemId === 'more' && onMoreClick) {
      onMoreClick();
    } else {
      onNavigate(itemId);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around h-16 max-w-screen-xl mx-auto">
        {/* Dynamic navigation items based on role */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentView === item.id;
          const isVisible = item.visible !== false;

          if (!isVisible) return null;

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.id)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="relative inline-flex">
                <Icon size={24} />
              </span>
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
